import { type LiveContextTransform } from '@framework/validation/live/LiveTypes.ts';
import { get, has } from 'lodash';
import { buildPathBySemanticFieldName } from './buildPathBySemanticFieldName.ts';

/**
 * Costruisce la transform che popola `context.semanticFields`.
 *
 * Input build-time: `semanticTagId -> rhfPath`.
 * Bridge runtime: `context.semanticTags` risolve `semanticTagId -> nomeTag`.
 * Output expression: `$semanticFields.<nomeTag>`.
 */
export function createSemanticFieldsContextTransform(
  selectedPathBySemanticTagId: Map<string, string>,
): LiveContextTransform {
  return (_schema, values, context) => {
    const pathBySemanticFieldName = buildPathBySemanticFieldName(
      selectedPathBySemanticTagId,
      context?.semanticTags ?? null,
    );

    const liveSemanticFields =
      pathBySemanticFieldName.size > 0
        ? readLiveSemanticFieldsFromFormValues(values, pathBySemanticFieldName)
        : null;

    // `VALIDATION_VALUES` non viene iniettato qui: ci pensa `livePatch.ts`
    // (centralizzato per tutti gli schemi) prima di invocare questa transform.
    return {
      ...context,
      ...(liveSemanticFields && {
        semanticFields: {
          ...context?.semanticFields,
          ...liveSemanticFields,
        },
      }),
    };
  };
}

/**
 * Legge dai valori RHF i campi esportati come semantic field.
 *
 * La chiave del risultato è il nome pubblico usato nelle expression
 * (`$semanticFields.<nomeTag>`), non l'id tecnico del semantic tag.
 */
function readLiveSemanticFieldsFromFormValues(
  values: any,
  pathBySemanticFieldName: Map<string, string>,
): Record<string, any> {
  const result: Record<string, any> = {};

  for (const [semanticFieldName, rhfPath] of pathBySemanticFieldName) {
    if (!has(values, rhfPath)) continue;
    result[semanticFieldName] = get(values, rhfPath);
  }

  return result;
}
